import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import HistorialPanel from './HistorialPanel';
import GeocercaManager from './GeocercaManager';
import ComandoPanel from './ComandoPanel';
import { cargarGoogleMaps, crearCapaBase } from './googleMapsTiles';

const TILE_DARK = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const TILE_OSM  = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_SAT  = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const TILE_URLS = { dark: TILE_DARK, osm: TILE_OSM, sat: TILE_SAT };

const TIPO_ICONO = { SEDAN:'🚗', SUV:'🛻', MOTO:'🏍️', CAMION:'🚚', DEFAULT:'📍' };

function iconoSvg(emoji, ignicion, velocidad) {
  const color = ignicion ? (velocidad > 0 ? '#4ade80' : '#fbbf24') : '#f87171';
  return window.L.divIcon({
    html: `<div style="
      font-size:22px; text-align:center; line-height:30px;
      text-shadow: 0 0 6px ${color}, 0 2px 4px rgba(0,0,0,.8);
      filter: drop-shadow(0 0 4px ${color});
    ">${emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    className: '',
  });
}

export default function MapaFlota({ usuario }) {
  const mapRef     = useRef(null);
  const mapaInst   = useRef(null);
  const marcadores = useRef({});
  const tileRef    = useRef(null);
  const clusterRef = useRef(null);
  // Ref para acceder a vehiculos actualizado dentro del callback SSE sin stale closure
  const vehiculosRef = useRef([]);

  const [tipoMapa,        setTipoMapa]        = useState('dark');
  const [vehiculos,       setVehiculos]        = useState([]);
  const [posicionesActivas, setPosicionesActivas] = useState({});  // imei → pos
  const [alertas,         setAlertas]          = useState([]);
  const [mostrarPanel,    setMostrarPanel]      = useState(true);
  const [modalVeh,        setModalVeh]          = useState(false);
  const [formVeh,         setFormVeh]           = useState({
    nombre:'', placas:'', imei_dispositivo:'', tipo:'SEDAN', color:'', notas:''
  });
  const [guardando,  setGuardando]  = useState(false);
  const [error,      setError]      = useState('');
  const [histVeh,    setHistVeh]    = useState(null);
  const [geoVeh,     setGeoVeh]     = useState(null);
  const [cmdVeh,     setCmdVeh]     = useState(null);
  const [editVeh,    setEditVeh]    = useState(null);  // vehicle being edited
  const [formEdit,   setFormEdit]   = useState({});

  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || mapaInst.current) return;
    let cancelado = false;
    cargarGoogleMaps().finally(() => {
      if (cancelado || !mapRef.current || mapaInst.current) return;
      const L = window.L;
      const mapa = L.map(mapRef.current, { center: [23.634501, -102.552784], zoom: 5 });
      tileRef.current = crearCapaBase('dark', L, TILE_URLS);
      tileRef.current.addTo(mapa);
      clusterRef.current = L.markerClusterGroup({ maxClusterRadius: 60 });
      mapa.addLayer(clusterRef.current);
      mapaInst.current = mapa;
    });
    return () => { cancelado = true; };
  }, []);

  // Cambiar tile
  useEffect(() => {
    if (!mapaInst.current) return;
    const L = window.L;
    if (tileRef.current) mapaInst.current.removeLayer(tileRef.current);
    tileRef.current = crearCapaBase(tipoMapa, L, TILE_URLS);
    tileRef.current.addTo(mapaInst.current);
    tileRef.current.bringToBack();
  }, [tipoMapa]);

  // Mantener ref de vehiculos sincronizada para los callbacks SSE
  useEffect(() => { vehiculosRef.current = vehiculos; }, [vehiculos]);

  const cargarVehiculos = useCallback(() => {
    axios.get('/api/v1/flota/vehiculos').then(r => setVehiculos(r.data)).catch(() => {});
  }, []);

  const cargarAlertas = useCallback(() => {
    axios.get('/api/v1/flota/alertas').then(r => setAlertas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    cargarVehiculos();
    cargarAlertas();
    const iv = setInterval(cargarAlertas, 30000);
    return () => clearInterval(iv);
  }, [cargarVehiculos, cargarAlertas]);

  // SSE: recibir posiciones en tiempo real
  useEffect(() => {
    const es = new EventSource('/stream/flota', { withCredentials: true });

    es.onmessage = (e) => {
      const posiciones = JSON.parse(e.data);
      if (!Array.isArray(posiciones)) return;

      // Construir mapa imei→vehículo usando el ref (no stale)
      const vehMap = {};
      vehiculosRef.current.forEach(v => { vehMap[v.imei_dispositivo] = v; });

      // Actualizar estado React para re-render del panel
      const nuevasPos = {};
      posiciones.forEach(pos => { if (pos.imei) nuevasPos[pos.imei] = pos; });
      setPosicionesActivas(nuevasPos);

      // Actualizar marcadores en el mapa
      actualizarMarcadores(posiciones, vehMap);
    };

    return () => es.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actualizarMarcadores = (posiciones, vehMap) => {
    if (!mapaInst.current || !clusterRef.current) return;
    const L = window.L;
    const imeisRecibidos = new Set();

    posiciones.forEach(pos => {
      const { imei, lat, lng, velocidad, ignicion, timestamp } = pos;
      if (!lat || !lng || !imei) return;
      imeisRecibidos.add(imei);

      const veh = vehMap[imei] || {};
      const nombre  = veh.nombre  || imei;
      const placas  = veh.placas  || '—';
      const tipo    = veh.tipo    || 'DEFAULT';

      const emoji = TIPO_ICONO[tipo] || TIPO_ICONO.DEFAULT;
      const icon  = iconoSvg(emoji, ignicion, velocidad || 0);

      const popup = `
        <div style="background:#0b132b;color:#e0e1dd;padding:10px;border-radius:8px;min-width:180px;font-size:13px">
          <div style="font-weight:700;color:#ff6600;margin-bottom:6px">${emoji} ${nombre}</div>
          <div>🔖 ${placas}</div>
          <div>🏎️ ${velocidad || 0} km/h</div>
          <div>🔑 ${ignicion ? '🟢 Encendido' : '🔴 Apagado'}</div>
          <div style="color:#8892a4;font-size:11px;margin-top:6px">${timestamp || ''}</div>
        </div>`;

      if (marcadores.current[imei]) {
        marcadores.current[imei].setLatLng([lat, lng]).setIcon(icon).setPopupContent(popup);
      } else {
        const m = L.marker([lat, lng], { icon }).bindPopup(popup);
        clusterRef.current.addLayer(m);
        marcadores.current[imei] = m;
      }
    });

    // Quitar marcadores de vehículos que desaparecieron
    Object.keys(marcadores.current).forEach(imei => {
      if (!imeisRecibidos.has(imei)) {
        clusterRef.current.removeLayer(marcadores.current[imei]);
        delete marcadores.current[imei];
      }
    });
  };

  const centrarEnVehiculo = (imei) => {
    const m = marcadores.current[imei];
    if (m && mapaInst.current) {
      mapaInst.current.setView(m.getLatLng(), 15);
      m.openPopup();
    }
  };

  const guardarVehiculo = async () => {
    setGuardando(true); setError('');
    try {
      await axios.post('/api/v1/flota/vehiculos', formVeh);
      setModalVeh(false);
      setFormVeh({ nombre:'', placas:'', imei_dispositivo:'', tipo:'SEDAN', color:'', notas:'' });
      cargarVehiculos();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const marcarAlertaLeida = async (id) => {
    await axios.post(`/api/v1/flota/alertas/${id}/leer`).catch(() => {});
    setAlertas(a => a.filter(x => x.id !== id));
  };

  const abrirEditar = (v) => {
    setFormEdit({ nombre: v.nombre, tipo: v.tipo, color: v.color || '', notas: v.notas || '', asignado_a: v.asignado_a || '' });
    setEditVeh(v);
  };

  const guardarEdicion = async () => {
    setGuardando(true); setError('');
    try {
      await axios.put(`/api/v1/flota/vehiculos/${editVeh.id}`, formEdit);
      setEditVeh(null);
      cargarVehiculos();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const eliminarVehiculo = async (v) => {
    if (!window.confirm(`¿Eliminar "${v.nombre}" (${v.placas})?\n\nEl vehículo se desactivará y dejará de recibir posiciones GPS.`)) return;
    try {
      await axios.delete(`/api/v1/flota/vehiculos/${v.id}`);
      cargarVehiculos();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const enLinea = (imei) => !!posicionesActivas[imei];
  const posVeh  = (imei) => posicionesActivas[imei] || null;

  return (
    <div style={{ display:'flex', height:'100%', position:'relative' }}>
      {/* Mapa */}
      <div ref={mapRef} style={{ flex:1, height:'100%' }} />

      {/* Selector de tiles */}
      <div style={{ position:'absolute', top:10, left:10, zIndex:1000, display:'flex', gap:6 }}>
        {[
          { id:'dark', label:'🌙 Noche' },
          { id:'osm',  label:'🗺️ OSM' },
          { id:'sat',  label:'🛰️ Satélite' },
        ].map(t => (
          <button key={t.id} onClick={() => setTipoMapa(t.id)} style={{
            padding:'5px 10px', border:'1px solid #1c2541', borderRadius:6,
            cursor:'pointer', fontSize:12, fontWeight:600,
            background: tipoMapa === t.id ? '#ff6600' : 'rgba(11,19,43,.9)',
            color:       tipoMapa === t.id ? '#fff'    : '#8892a4',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Alertas flotantes */}
      {alertas.length > 0 && (
        <div style={{
          position:'absolute', top:10, right: mostrarPanel ? 320 : 10,
          zIndex:1000, maxWidth:280
        }}>
          {alertas.slice(0, 3).map(a => (
            <div key={a.id} style={{
              background:'rgba(67,20,7,.95)', border:'1px solid #ff6600',
              borderRadius:8, padding:'8px 12px', marginBottom:6,
              display:'flex', alignItems:'flex-start', gap:8
            }}>
              <span>⚠️</span>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, color:'#ff6600', fontSize:12 }}>{a.tipo}</div>
                <div style={{ fontSize:11, color:'#e0e1dd' }}>
                  {a.vehiculo_nombre} · {a.mensaje}
                </div>
              </div>
              <button onClick={() => marcarAlertaLeida(a.id)}
                style={{ background:'none', border:'none', color:'#8892a4',
                         cursor:'pointer', fontSize:14, lineHeight:1 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Toggle panel */}
      <button onClick={() => setMostrarPanel(p => !p)} style={{
        position:'absolute', top:'50%', right: mostrarPanel ? 310 : 0,
        zIndex:1001, background:'#0b132b', border:'1px solid #1c2541',
        color:'#00c8ff', padding:'12px 4px', cursor:'pointer',
        borderRadius:'6px 0 0 6px', fontSize:12,
        writingMode:'vertical-rl', transform:'translateY(-50%)'
      }}>
        {mostrarPanel ? '▶ Cerrar' : '◀ Panel'}
      </button>

      {/* Panel lateral */}
      {mostrarPanel && histVeh && (
        <div style={{ width:340, borderLeft:'1px solid #1c2541', flexShrink:0 }}>
          <HistorialPanel tipo="flota" entidadId={histVeh.id} entidad={histVeh}
            mapaInst={mapaInst} esAdmin={usuario.es_admin}
            onClose={() => setHistVeh(null)} />
        </div>
      )}
      {mostrarPanel && geoVeh && !histVeh && !cmdVeh && (
        <div style={{ width:340, borderLeft:'1px solid #1c2541', flexShrink:0 }}>
          <GeocercaManager tipo="flota" entidadId={geoVeh.id}
            mapaInst={mapaInst} esAdmin={usuario.es_admin}
            onClose={() => setGeoVeh(null)} />
        </div>
      )}
      {mostrarPanel && cmdVeh && !histVeh && !geoVeh && (
        <div style={{ width:340, borderLeft:'1px solid #1c2541', flexShrink:0 }}>
          <ComandoPanel vehiculoId={cmdVeh.id} vehiculo={cmdVeh}
            onClose={() => setCmdVeh(null)} />
        </div>
      )}
      {mostrarPanel && !histVeh && !geoVeh && !cmdVeh && (
        <div style={{
          width:310, background:'#0b132b', borderLeft:'1px solid #1c2541',
          display:'flex', flexDirection:'column', flexShrink:0
        }}>
          <div style={{
            padding:'12px 16px', borderBottom:'1px solid #1c2541',
            display:'flex', justifyContent:'space-between', alignItems:'center'
          }}>
            <span style={{ fontWeight:700, color:'#ff6600' }}>
              🚗 Flota ({vehiculos.length})
              {Object.keys(posicionesActivas).length > 0 && (
                <span style={{
                  marginLeft:8, background:'#052e16', color:'#4ade80',
                  padding:'1px 7px', borderRadius:12, fontSize:11
                }}>
                  {Object.keys(posicionesActivas).length} en línea
                </span>
              )}
            </span>
            {usuario.es_admin && (
              <button className="btn btn-gps" onClick={() => setModalVeh(true)}
                style={{ fontSize:12, padding:'4px 10px' }}>
                + Vehículo
              </button>
            )}
          </div>

          <div style={{ flex:1, overflow:'auto', padding:8 }}>
            {vehiculos.length === 0 ? (
              <div style={{ color:'#8892a4', textAlign:'center', padding:24, fontSize:13 }}>
                {usuario.es_admin
                  ? 'Sin vehículos registrados.\nAgrega el primero con + Vehículo.'
                  : 'Sin vehículos asignados.'}
              </div>
            ) : vehiculos.map(v => {
              const activo = enLinea(v.imei_dispositivo);
              const pos    = posVeh(v.imei_dispositivo);
              return (
                <div key={v.id}
                  onClick={() => centrarEnVehiculo(v.imei_dispositivo)}
                  style={{
                    background:'#04080f', border:'1px solid #1c2541',
                    borderRadius:8, padding:10, marginBottom:6,
                    cursor: activo ? 'pointer' : 'default',
                    opacity: activo ? 1 : 0.65,
                  }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontWeight:600 }}>
                      {TIPO_ICONO[v.tipo] || '📍'} {v.nombre}
                    </span>
                    <span style={{
                      background: activo ? '#052e16' : '#101d3a',
                      color:      activo ? '#4ade80' : '#8892a4',
                      padding:'2px 6px', borderRadius:12, fontSize:10, fontWeight:700
                    }}>
                      {activo ? '● EN LÍNEA' : '○ SIN SEÑAL'}
                    </span>
                  </div>
                  <div style={{ color:'#8892a4', fontSize:11, marginTop:4 }}>
                    🔖 {v.placas}
                    {pos && (
                      <>
                        {' · '}🏎️ {pos.velocidad || 0} km/h
                        {' · '}{pos.ignicion ? '🟢 ON' : '🔴 OFF'}
                        {pos.bateria_pct !== undefined && ` · 🔋 ${pos.bateria_pct}%`}
                      </>
                    )}
                  </div>
                  {pos && pos.timestamp && (
                    <div style={{ color:'#8892a4', fontSize:10, marginTop:2 }}>
                      {new Date(pos.timestamp.endsWith('Z') ? pos.timestamp : pos.timestamp+'Z')
                        .toLocaleString('es-MX', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false})}
                    </div>
                  )}
                  <div style={{ display:'flex', gap:4, marginTop:6 }}>
                    <button onClick={(e) => { e.stopPropagation(); setHistVeh(v); }}
                      style={{ background:'#1c2541', border:'none', color:'#00c8ff',
                               borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10 }}>
                      📊 Historial
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setGeoVeh(v); }}
                      style={{ background:'#1c2541', border:'none', color:'#ff6600',
                               borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10 }}>
                      🔲 Geocercas
                    </button>
                    {usuario.es_admin && (
                      <button onClick={(e) => { e.stopPropagation(); setCmdVeh(v); }}
                        style={{ background:'#1c2541', border:'none', color:'#4ade80',
                                 borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10 }}>
                        📡 Comandos
                      </button>
                    )}
                    {usuario.es_admin && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); abrirEditar(v); }}
                          style={{ background:'#1c2541', border:'none', color:'#8892a4',
                                   borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10 }}>
                          ✏️ Editar
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); eliminarVehiculo(v); }}
                          style={{ background:'none', border:'none', color:'#f87171',
                                   borderRadius:5, padding:'3px 8px', cursor:'pointer', fontSize:10 }}>
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            padding:'8px 16px', borderTop:'1px solid #1c2541',
            display:'flex', justifyContent:'space-between', alignItems:'center'
          }}>
            {alertas.length > 0 ? (
              <span style={{ color:'#ff6600', fontSize:12 }}>
                ⚠️ {alertas.length} alerta{alertas.length > 1 ? 's' : ''}
              </span>
            ) : (
              <span style={{ color:'#1c2541', fontSize:11 }}>Sin alertas</span>
            )}
            <span style={{ color:'#1c2541', fontSize:10 }}>Actualiza cada 5s</span>
          </div>
        </div>
      )}

      {/* Modal nuevo vehículo */}
      {modalVeh && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.7)',
          zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'
        }}>
          <div style={{
            background:'#0b132b', border:'1px solid #1c2541',
            borderRadius:12, padding:28, width:440
          }}>
            <h2 style={{ color:'#ff6600', marginBottom:20, fontSize:16 }}>
              🚗 Registrar Vehículo
            </h2>

            {[
              { id:'nombre',           label:'Nombre / Alias',        ph:'Unidad Alpha' },
              { id:'placas',           label:'Placas',                ph:'ABC-123-D' },
              { id:'imei_dispositivo', label:'IMEI del FMC920/FMB920', ph:'862892059193523' },
              { id:'color',            label:'Color',                 ph:'Blanco' },
              { id:'notas',            label:'Notas',                 ph:'Zona Norte' },
            ].map(f => (
              <div key={f.id} style={{ marginBottom:12 }}>
                <label>{f.label}</label>
                <input
                  value={formVeh[f.id]}
                  onChange={e => setFormVeh(p => ({ ...p, [f.id]: e.target.value }))}
                  placeholder={f.ph}
                />
              </div>
            ))}

            <div style={{ marginBottom:16 }}>
              <label>Tipo de vehículo</label>
              <select value={formVeh.tipo}
                onChange={e => setFormVeh(p => ({ ...p, tipo: e.target.value }))}>
                <option value="SEDAN">🚗 Sedán</option>
                <option value="SUV">🛻 SUV / Pickup</option>
                <option value="MOTO">🏍️ Motocicleta</option>
                <option value="CAMION">🚚 Camión / Van</option>
              </select>
            </div>

            {error && (
              <p style={{ color:'#f87171', fontSize:12, marginBottom:12 }}>{error}</p>
            )}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-ghost"
                onClick={() => { setModalVeh(false); setError(''); }}>
                Cancelar
              </button>
              <button className="btn btn-gps" onClick={guardarVehiculo} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar vehículo */}
      {editVeh && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.7)',
          zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'
        }}>
          <div style={{
            background:'#0b132b', border:'1px solid #1c2541',
            borderRadius:12, padding:28, width:440
          }}>
            <h2 style={{ color:'#ff6600', marginBottom:20, fontSize:16 }}>
              ✏️ Editar — {editVeh.nombre}
            </h2>
            <div style={{ color:'#8892a4', fontSize:11, marginBottom:16 }}>
              🔖 {editVeh.placas} · IMEI: {editVeh.imei_dispositivo}
            </div>

            {[
              { id:'nombre', label:'Nombre / Alias' },
              { id:'color',  label:'Color' },
              { id:'notas',  label:'Notas' },
            ].map(f => (
              <div key={f.id} style={{ marginBottom:12 }}>
                <label>{f.label}</label>
                <input value={formEdit[f.id] || ''}
                  onChange={e => setFormEdit(p => ({ ...p, [f.id]: e.target.value }))} />
              </div>
            ))}

            <div style={{ marginBottom:16 }}>
              <label>Tipo de vehículo</label>
              <select value={formEdit.tipo || 'SEDAN'}
                onChange={e => setFormEdit(p => ({ ...p, tipo: e.target.value }))}>
                <option value="SEDAN">🚗 Sedán</option>
                <option value="SUV">🛻 SUV / Pickup</option>
                <option value="MOTO">🏍️ Motocicleta</option>
                <option value="CAMION">🚚 Camión / Van</option>
              </select>
            </div>

            {error && <p style={{ color:'#f87171', fontSize:12, marginBottom:12 }}>{error}</p>}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-ghost"
                onClick={() => { setEditVeh(null); setError(''); }}>Cancelar</button>
              <button className="btn btn-gps" onClick={guardarEdicion} disabled={guardando}>
                {guardando ? 'Guardando...' : '💾 Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
